/**
 * Storage Manager - IndexedDB wrapper for save/load functionality
 */

import type { SaveData } from './SaveData';
import { SAVE_VERSION, MAX_SAVE_SLOTS, createEmptySaveData } from './SaveData';

const DB_NAME = 'OneRealmSaves';
const DB_VERSION = 1;
const STORE_NAME = 'saves';

export interface SaveSlot {
  slot: number;
  data: SaveData | null;
  isEmpty: boolean;
}

export class StorageManager {
  private db: IDBDatabase | null = null;
  private initialized: boolean = false;

  /**
   * Initialize the storage manager
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.initialized = true;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'slot' });
          store.createIndex('timestamp', 'data.timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Save game data to a slot
   */
  async save(slot: number, data: SaveData): Promise<boolean> {
    if (!this.db || slot < 0 || slot >= MAX_SAVE_SLOTS) return false;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const saveSlot: SaveSlot = {
        slot,
        data: { ...data, timestamp: Date.now(), version: SAVE_VERSION },
        isEmpty: false,
      };

      const request = store.put(saveSlot);

      request.onsuccess = () => resolve(true);
      request.onerror = () => {
        console.error('Failed to save:', request.error);
        resolve(false);
      };
    });
  }

  /**
   * Load game data from a slot
   */
  async load(slot: number): Promise<SaveData | null> {
    if (!this.db || slot < 0 || slot >= MAX_SAVE_SLOTS) return null;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(slot);

      request.onsuccess = () => {
        const result = request.result as SaveSlot | undefined;
        if (result && result.data) {
          // Version migration if needed
          if (result.data.version !== SAVE_VERSION) {
            resolve(this.migrateSaveData(result.data));
          } else {
            resolve(result.data);
          }
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('Failed to load:', request.error);
        resolve(null);
      };
    });
  }

  /**
   * Delete a save slot
   */
  async delete(slot: number): Promise<boolean> {
    if (!this.db || slot < 0 || slot >= MAX_SAVE_SLOTS) return false;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(slot);

      request.onsuccess = () => resolve(true);
      request.onerror = () => {
        console.error('Failed to delete:', request.error);
        resolve(false);
      };
    });
  }

  /**
   * Get all save slots
   */
  async getAllSlots(): Promise<SaveSlot[]> {
    const slots: SaveSlot[] = [];

    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      slots.push({
        slot: i,
        data: null,
        isEmpty: true,
      });
    }

    if (!this.db) return slots;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const results = request.result as SaveSlot[];
        for (const result of results) {
          if (result.slot >= 0 && result.slot < MAX_SAVE_SLOTS) {
            slots[result.slot] = result;
          }
        }
        resolve(slots);
      };

      request.onerror = () => {
        console.error('Failed to get slots:', request.error);
        resolve(slots);
      };
    });
  }

  /**
   * Migrate save data from older versions
   */
  private migrateSaveData(data: SaveData): SaveData {
    // For now, just return empty save if version mismatch
    console.warn('Save version mismatch, creating new save');
    return createEmptySaveData();
  }

  /**
   * Export save as JSON string
   */
  exportSave(data: SaveData): string {
    return JSON.stringify(data);
  }

  /**
   * Import save from JSON string
   */
  importSave(jsonString: string): SaveData | null {
    try {
      const data = JSON.parse(jsonString) as SaveData;
      if (data.version && data.entities && data.factionStates) {
        return data;
      }
    } catch (e) {
      console.error('Failed to import save:', e);
    }
    return null;
  }
}

// Singleton instance
export const storageManager = new StorageManager();
